/**
 * Returns whether an encounter is still within its edit window, using the
 * server-computed `editableUntil` instant (encounter createdAt + the configured
 * edit window). The window length lives only on encounter-service; the UI never
 * hardcodes it.
 *
 * Half-open window: editable while `now < editableUntil`. At/after the boundary
 * it is closed — matching encounter-service's server-side rule (spec 024). A
 * 0-length window yields `editableUntil === createdAt`, so the encounter is
 * never editable (prod has no edit window).
 *
 * @param editableUntil  ISO datetime from `EncounterResponse.editableUntil` or
 *                       `CaseListRow.editableUntil`. Null/undefined/invalid → not editable.
 * @param now  Current time as ms-since-epoch. Defaults to `Date.now()`. Injectable
 *             for deterministic testing.
 */
export function isWithinEditWindow(
  editableUntil: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!editableUntil) return false;
  const until = Date.parse(editableUntil);
  if (Number.isNaN(until)) return false;
  return now < until;
}

/**
 * Human-readable phrase for the CONFIGURED edit-window length. The single source
 * of truth is encounter-service's `EncounterPortalEditWindow` config, fetched once via
 * `GET /api/v1/encounters/config` (`encounterPortalEditWindowMinutes`) — the client never
 * hardcodes a duration.
 *
 * Returns e.g. `"12-hour edit window"`, `"3-minute edit window"`; falls back to
 * `"edit window"` when the duration is unknown (config not loaded yet) or non-positive.
 */
export function describeEditWindow(minutes: number | null | undefined): string {
  const fallback = 'edit window';
  if (minutes === null || minutes === undefined || minutes <= 0) return fallback;
  const rounded = Math.round(minutes);
  if (rounded < 60) return `${rounded}-minute edit window`;
  const hours = Math.round(rounded / 60);
  return `${hours}-hour edit window`;
}
